import { FALLBACK_LANGUAGE } from '@/shared/i18n/constants';
import { localeToLang } from '@/shared/i18n/localeToLang';
import { Language } from '@/shared/model/Language';
import { Page } from './component-id';
import { replaceParams } from './getStaticPageUrl';

/**
 * Default page-builder URL pattern for the order thank-you page.
 */
export const DEFAULT_ORDER_PAGE_URL_PATTERN = '/thank-you/[ORDER_ID]';

const DEFAULT_ORDER_URL_BY_LOCALE: Partial<Record<Language, string>> = {
    [Language.CS]: '/dekujeme/[ORDER_ID]',
    [Language.EN_GB]: DEFAULT_ORDER_PAGE_URL_PATTERN,
    [Language.SK]: '/dakujeme/[ORDER_ID]',
};

/** Config pages from JSON are loosely typed; only `metadata.url` matters for routing. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type LoosePages = Record<string, any> | null | undefined;

/** Minimal page injected when the website config has no `order` key. */
const ORDER_PAGE_FALLBACK: Page = {
    metadata: {
        title: {
            [Language.EN_GB]: 'Order',
        },
        url: { ...DEFAULT_ORDER_URL_BY_LOCALE } as Page['metadata']['url'],
    },
};

/** Order ids used in paths / redirects (UUID-like / alphanumeric). */
const ORDER_ID_RE = /^[A-Za-z0-9_-]{1,128}$/;

export function isValidOrderId(orderId: string): boolean {
    return ORDER_ID_RE.test(orderId);
}

/**
 * Ensures `pages.order` exists for matching and rendering (tenant configs may predate the page).
 */
export function withOrderPageFallback(pages: LoosePages): Record<string, Page> {
    return {
        ...(pages ?? {}),
        order: pages?.order ?? ORDER_PAGE_FALLBACK,
    } as Record<string, Page>;
}

/** Same-origin relative path only (blocks open redirects). */
export function isSafeRelativePath(path: string): boolean {
    if (!path.startsWith('/') || path.startsWith('//')) {
        return false;
    }
    if (path.includes('\\') || /[\0-\x1f]/.test(path)) {
        return false;
    }
    // Reject scheme-looking prefixes like "javascript:" if somehow present after "/".
    if (/^\/[a-zA-Z][a-zA-Z0-9+.-]*:/.test(path)) {
        return false;
    }
    return true;
}

/**
 * Resolves the customer-facing order landing path from website config.
 * Rejects unsafe absolute/open-redirect URLs.
 */
export function resolveOrderLandingPath(
    website: { pages?: LoosePages } | null | undefined,
    locale: Language,
    orderId: string,
): string {
    const safeId = isValidOrderId(orderId) ? orderId : '';
    if (!safeId) {
        return '/';
    }

    const pages = withOrderPageFallback(website?.pages);
    const pattern =
        pages.order?.metadata?.url?.[locale] ??
        DEFAULT_ORDER_URL_BY_LOCALE[locale] ??
        DEFAULT_ORDER_PAGE_URL_PATTERN;
    const path = replaceParams(pattern, { ORDER_ID: encodeURIComponent(safeId) });
    const pathname = path.split('?')[0] ?? path;

    if (!isSafeRelativePath(pathname)) {
        return replaceParams(DEFAULT_ORDER_PAGE_URL_PATTERN, { ORDER_ID: encodeURIComponent(safeId) });
    }
    return path;
}

/**
 * Vrátí mapu dynamických parametrů, pokud pattern matchuje pathname.
 */
function matchDynamicParams(pattern: string, pathname: string): Record<string, string> | null {
    const paramNames = Array.from(pattern.matchAll(/\[([^\]]+)\]/g)).map((match) => match[1]);

    if (paramNames.length === 0) return null;

    const escaped = pattern
        .replace(/[.*+?^${}()|[\]\\]/g, (char) => {
            if (char === '[' || char === ']') return char;
            return '\\' + char;
        })
        .replace(/\[[^\]]+\]/g, '([^/]+)');

    const regex = new RegExp(`^${escaped}$`);
    const match = regex.exec(pathname);
    if (!match) return null;

    const params: Record<string, string> = {};
    paramNames.forEach((name, index) => {
        params[name] = match[index + 1] ?? '';
    });

    return params;
}

/**
 * Vybere správnou page z Website objektu podle aktuální URL a locale
 */
export function getPageByUrl(
    website: { pages?: LoosePages } | null,
    pathname: string,
    locale: Language,
): { page: Page; pageName: string; params: Record<string, string> } {
    const normalizedPathname = pathname === '' ? '/' : pathname;
    const pages = withOrderPageFallback(website?.pages);
    const pagesEntries = Object.entries(pages);

    const exactMatch = pagesEntries.find(([, page]) => {
        const pageUrl = page.metadata.url?.[locale] ?? page.metadata.url?.[localeToLang(FALLBACK_LANGUAGE)];
        return pageUrl === normalizedPathname;
    });

    if (exactMatch) {
        return { page: exactMatch[1], pageName: exactMatch[0], params: {} };
    }

    let dynamicMatch: [string, Page] | undefined;
    let dynamicParams: Record<string, string> = {};
    for (const entry of pagesEntries) {
        const pageUrl =
            entry[1].metadata.url?.[locale] ?? entry[1].metadata.url?.[localeToLang(FALLBACK_LANGUAGE)];
        if (!pageUrl) continue;

        if (pageUrl.includes('[') && pageUrl.includes(']')) {
            const params = matchDynamicParams(pageUrl, normalizedPathname);
            if (params) {
                dynamicMatch = entry;
                dynamicParams = params;
                break;
            }
        }
    }

    const fallback = pagesEntries[0];
    return {
        page: dynamicMatch?.[1] || fallback?.[1],
        pageName: dynamicMatch?.[0] || fallback?.[0] || '',
        params: dynamicMatch ? dynamicParams : {},
    };
}
